import React from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { GridFeeStateButtonGroup } from '../GridFeeStateButtonGroup'
import { GridFeeState } from '../../../dto'
import VariableGridFeeSettingsForm from './VariableGridFeeSettingsForm'
import StaticGridFeeSettingsForm from './StaticGridFeeSettingsForm'
import { DynamicTariffSettingsFormFields } from 'src/Libs/dynamic-tariff-settings/constants'
import { Stack } from '@mui/system'
import { Button, Typography } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useApp } from 'src/Hooks'

interface Props {
  hasWritingRights: boolean
}

/**
 * Component to show grid fee settings for dynamic tariff settings form
 */
const GridFeesForm: React.FC<Props> = ({ hasWritingRights }) => {
  const { control, formState } = useFormContext()
  const { t } = useCustomTranslation()
  const { isMobile: isDownSm } = useApp()
  const gridFeeState = useWatch({ control, name: DynamicTariffSettingsFormFields.GridFeeState })
  const { fields, append, remove } = useFieldArray({
    name: DynamicTariffSettingsFormFields.GridFees,
    control,
  })

  /* add new date frame with default date values */
  const handleAddDataFrame = (): void => {
    /* default 'from': Jan-1-1972, 'to': Dec-31-1972  */
    append({ from: new Date(1972, 0, 1, 0, 0, 0, 0), to: new Date(1972, 11, 31, 0, 0, 0, 0), timeFrames: [] })
  }

  const formInvalid = formState.errors.gridFees

  return (
    <>
      <Stack
        sx={{
          border: formInvalid ? '1px solid' : '',
          borderColor: formInvalid ? 'error.main' : 'transparent',
          borderRadius: 1,
          mb: 2,
        }}
      >
        <GridFeeStateButtonGroup disabled={!hasWritingRights} />
        {gridFeeState === GridFeeState.Variable && (
          <VariableGridFeeSettingsForm fields={fields} remove={remove} hasWritingRights={hasWritingRights} />
        )}
        {gridFeeState === GridFeeState.Static && <StaticGridFeeSettingsForm hasWritingRights={hasWritingRights} />}
      </Stack>

      {gridFeeState === GridFeeState.Variable && (
        <Stack direction={isDownSm ? 'column' : 'row'} justifyContent="space-between" alignItems="center">
          <Typography sx={{ color: 'error.main' }} variant="body2">
            {formInvalid ? t('dynamic-tariff-settings.validation-message') : ''}
          </Typography>
          <Button
            disabled={!hasWritingRights}
            sx={{ mt: isDownSm ? 2 : 0, width: isDownSm ? '100%' : 'fit-content' }}
            onClick={() => handleAddDataFrame()}
          >
            {t('dynamic-tariff-settings.add-date-frame')}
          </Button>
        </Stack>
      )}
    </>
  )
}

export default GridFeesForm
